// Screen 1 — Meal Planner entrance.
// Compact application entry, not a marketing homepage clone. Customers
// arrive here from the WordPress site; they've already seen the story.

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { BrandButton } from '../components/BrandButton';

const STEPS = [
  { n: '01', title: 'Tell us your goals' },
  { n: '02', title: 'Choose your plan' },
  { n: '03', title: 'Pick up your meals' },
];

const TRUST = ['Gluten Free', 'Seed Oil Free', 'Avocado Oil Only'];

export default function Welcome() {
  const navigate = useNavigate();

  return (
    <AppShell>
      <div className="mx-auto w-full" style={{ maxWidth: '1120px' }}>
        {/* Main content: two columns on desktop, one on mobile ------ */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Left column ------------------------------------------- */}
          <div className="md:col-span-7 order-1">
            <div className="eyebrow" data-testid="welcome-eyebrow">
              Personalized Meal Planning
            </div>
            <h1
              className="h1 mt-4"
              data-testid="welcome-headline"
            >
              Build a plan around your goals
            </h1>
            <p
              className="serif mt-4 leading-snug"
              style={{ fontSize: 'clamp(19px, 2vw, 22px)', color: 'var(--text-dim)' }}
              data-testid="welcome-accent-copy"
            >
              Fresh meals selected for your schedule, goals, and preferences.
            </p>
            <p
              className="mt-5"
              style={{ color: 'var(--ink-2)', maxWidth: '54ch' }}
              data-testid="welcome-body-copy"
            >
              Choose your days, calorie target, dietary needs, and meal
              preferences. We&apos;ll create three personalized plans using
              real Eat This meals.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <BrandButton
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => navigate('/plan/days')}
                data-testid="welcome-start-btn"
                className="sm:w-auto"
              >
                Start my meal plan
              </BrandButton>
              <span
                className="text-sm"
                style={{ color: 'var(--text-dim)' }}
                data-testid="welcome-time-hint"
              >
                Takes about 3 minutes. No payment until checkout.
              </span>
            </div>

            <div className="mt-5">
              <Link
                to="/sign-in"
                className="text-sm underline underline-offset-4 hover:opacity-70 transition-opacity"
                style={{ color: 'var(--ink)' }}
                data-testid="welcome-signin-link"
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>

          {/* Right column ------------------------------------------ */}
          <div className="md:col-span-5 order-2">
            <div
              className="card-offset overflow-hidden mx-auto"
              style={{
                width: '100%',
                maxWidth: '340px',
                aspectRatio: '4 / 5',
                background:
                  'linear-gradient(135deg, var(--cream-2), var(--cream-3))',
              }}
              data-testid="welcome-hero-image"
            >
              <img
                src="https://eatthisthewoodlands.com/wp-content/uploads/2026/05/IMG_7218-scaled.jpeg"
                alt="A fresh Eat This meal"
                className="w-full h-full object-cover"
                loading="eager"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        </section>

        {/* Three-step explainer ---------------------------------- */}
        <section
          className="mt-14 md:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8"
          data-testid="welcome-steps"
        >
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="flex items-start gap-4"
              data-testid={`welcome-step-${s.n}`}
            >
              <div
                className="font-display leading-none"
                style={{
                  color: 'var(--orange)',
                  fontSize: '34px',
                  letterSpacing: '0.06em',
                  minWidth: '48px',
                }}
              >
                {s.n}
              </div>
              <div>
                <div
                  className="font-display uppercase"
                  style={{
                    fontSize: '18px',
                    letterSpacing: '0.12em',
                    color: 'var(--ink)',
                    lineHeight: 1.15,
                  }}
                >
                  {s.title}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Static trust strip ------------------------------------ */}
        <div
          className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-6"
          style={{ borderTop: '1px solid var(--line)' }}
          data-testid="welcome-trust-strip"
        >
          {TRUST.map((t, i) => (
            <React.Fragment key={t}>
              <span
                className="eyebrow"
                style={{ color: 'var(--ink)' }}
                data-testid={`welcome-trust-${t.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {t}
              </span>
              {i < TRUST.length - 1 && (
                <span
                  aria-hidden
                  style={{
                    color: 'var(--orange)',
                    letterSpacing: '0.32em',
                  }}
                >
                  ·
                </span>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Trainer Program entry point — secondary, non-competing with the primary customer CTA */}
        <div
          className="mt-10 md:mt-14 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }}
          data-testid="welcome-trainer-program-card"
        >
          <div>
            <div
              className="font-display uppercase"
              style={{ fontSize: '18px', letterSpacing: '0.12em', color: 'var(--ink)' }}
              data-testid="welcome-trainer-program-eyebrow"
            >
              Trainer Program
            </div>
            <p className="mt-2 text-sm" style={{ color: 'var(--text-dim)', maxWidth: '48ch' }} data-testid="welcome-trainer-program-copy">
              Are you a trainer or fitness professional? Apply to join the Eat This Fast &amp; Fresh Trainer Program.
            </p>
          </div>
          <BrandButton
            variant="outline"
            size="sm"
            onClick={() => navigate('/trainer/apply')}
            className="whitespace-nowrap"
            data-testid="welcome-trainer-program-cta"
          >
            Apply as a Trainer
          </BrandButton>
        </div>
      </div>
    </AppShell>
  );
}
